'use client';

import { AppImageClient } from '@components/appImage';
import { MainContainer } from '@components/mainContainer';
import { Sanitizer } from '@components/sanitizer';
import { Title } from '@components/title';
import { Typography } from '@components/typography';
import { cn } from '@lib/utils/cn';

import { MainButton } from './MainButton';
import { SecondaryButton } from './SecondaryButton';
import { AnimationType, MainTitleBlockProps } from './types';

import { useUtmHref } from '@/shared/hooks';

export const MainTitleBlock = ({
  titleId,
  title,
  descriptionId,
  description,
  mainButtonContent,
  mainButtonHref,
  mainButtonVariant,
  secondaryButtonContent,
  secondaryButtonHref,
  secondaryButtonAnalyticsEvent,
  secondaryButtonVariant,
  imgSrc,
  imageData,
  animationType = 'float',
  descriptionWithTooltip,
  customClassNames = {},
  customButtonComponent,
  width,
  height,
}: MainTitleBlockProps) => {
  const mainButtonHrefWithUtm = useUtmHref(mainButtonHref);
  const secondaryButtonHrefWithUtm = useUtmHref(secondaryButtonHref);

  return (
    <section
      className={cn('pt-16 pb-16 sm:pt-0 lg:h-[552px]', customClassNames.main)}
    >
      <MainContainer className="flex h-full flex-col-reverse items-center justify-between gap-4 lg:flex-row">
        <div className={cn('sm:max-w-[564px]', customClassNames.wrapperTitle)}>
          <Title id={titleId} className="w-full">
            {title}
          </Title>
          {description ? (
            <Typography
              as="div"
              variant="accent-m"
              id={descriptionId}
              className={cn(
                'mb-12 max-sm:mb-8 [&_a]:cursor-pointer [&_a]:underline [&_a]:underline-offset-[3px]',
                customClassNames.description,
              )}
            >
              <Sanitizer htmlContent={description} />
            </Typography>
          ) : (
            <div
              className={cn(
                'mb-12 max-w-[501px] text-base tracking-[-0.01rem] max-sm:mb-8 [&_a]:cursor-pointer [&_a]:underline',
                customClassNames.description,
              )}
            >
              {descriptionWithTooltip}
            </div>
          )}
          <div
            className={cn(
              'flex w-full flex-wrap items-center gap-4',
              customClassNames.wrapperButton,
            )}
          >
            {customButtonComponent ?? (
              <>
                {secondaryButtonContent && (
                  <SecondaryButton
                    secondaryButtonHrefWithUtm={secondaryButtonHrefWithUtm}
                    className={customClassNames.secondaryButton}
                    secondaryButtonContent={secondaryButtonContent}
                    secondaryButtonAnalyticsEvent={
                      secondaryButtonAnalyticsEvent
                    }
                    secondaryButtonHref={secondaryButtonHref}
                    secondaryButtonVariant={secondaryButtonVariant}
                  />
                )}
                {mainButtonContent && (
                  <MainButton
                    mainButtonHrefWithUtm={mainButtonHrefWithUtm}
                    className={customClassNames.primaryButton}
                    mainButtonContent={mainButtonContent}
                    mainButtonHref={mainButtonHref}
                    mainButtonVariant={mainButtonVariant}
                  />
                )}
              </>
            )}
          </div>
        </div>
        {imageData ? (
          <AppImageClient
            {...imageData}
            className={cn(
              'max-h-[488px] w-auto lg:h-5/6 xl:h-full',
              customClassNames.img,
            )}
            alt="main_title_block_image"
            style={{
              animation: getAnimation(animationType),
            }}
            priority
            width={width}
            height={height}
          />
        ) : (
          imgSrc && (
            <img
              className={cn(
                'max-h-[488px] w-auto lg:h-5/6 xl:h-full',
                customClassNames.img,
              )}
              src={imgSrc}
              alt="main_title_block_image"
              style={{
                animation: getAnimation(animationType),
              }}
              decoding="async"
              loading="eager"
              fetchPriority="high"
              width={width}
              height={height}
            />
          )
        )}
      </MainContainer>

      <style jsx global>{`
        @keyframes float {
          0% {
            transform: translateY(0px);
          }
          50% {
            transform: translateY(25px);
          }
          100% {
            transform: translateY(0px);
          }
        }

        @keyframes pulse {
          0% {
            transform: scale(1);
          }
          50% {
            transform: scale(1.05);
          }
          100% {
            transform: scale(1);
          }
        }

        @keyframes rotate {
          0% {
            transform: rotate(-5deg);
          }
          50% {
            transform: rotate(5deg);
          }
          100% {
            transform: rotate(-5deg);
          }
        }

        @keyframes swing {
          0% {
            transform: perspective(800px) rotateY(0);
          }
          50% {
            transform: perspective(800px) rotateY(15deg);
          }
          100% {
            transform: perspective(800px) rotateY(0);
          }
        }

        @keyframes bounce {
          0%,
          100% {
            transform: translateY(0);
            animation-timing-function: cubic-bezier(0.8, 0, 1, 1);
          }
          50% {
            transform: translateY(25px);
            animation-timing-function: cubic-bezier(0, 0, 0.2, 1);
          }
        }
      `}</style>
    </section>
  );
};

const getAnimation = (type: AnimationType): string => {
  switch (type) {
    case 'float':
      return 'float 4s linear infinite';
    case 'pulse':
      return 'pulse 2s ease-in-out infinite';
    case 'rotate':
      return 'rotate 6s cubic-bezier(0.45, 0, 0.55, 1) infinite';
    case 'swing':
      return 'swing 4s ease-in-out infinite';
    case 'bounce':
      return 'bounce 1.5s infinite';
    case 'none':
    default:
      return 'none';
  }
};
